import { useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'

export default function Layout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { user, logout, points, fetchPoints } = useStore()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (user) {
      fetchPoints()
    }
  }, [user, fetchPoints])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Navigation items
  const navItems = [
    { path: '/', label: t('nav.home'), icon: 'home' },
    { path: '/matches', label: t('nav.matches'), icon: 'heart', matchPaths: ['/matches', '/chat'] },
    { path: '/community', label: t('nav.community', 'Community'), icon: 'users', matchPaths: ['/community'] },
    { path: '/my-persona', label: t('nav.myPersona'), icon: 'bot', matchPaths: ['/my-persona'] },
    { path: '/profile', label: t('nav.profile'), icon: 'user' },
  ]

  const isNavActive = (item: typeof navItems[0]) => {
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname.startsWith(p))
    }
    return location.pathname === item.path
  }

  const renderIcon = (icon: string, isActive: boolean) => {
    const className = `w-5 h-5 transition-colors ${isActive ? 'text-primary' : 'text-base-content/50'}`

    switch (icon) {
      case 'home':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isActive ? 2.5 : 2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        )
      case 'heart':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className={className} fill={isActive ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isActive ? 0 : 2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        )
      case 'users':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isActive ? 2.5 : 2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      case 'bot':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isActive ? 2.5 : 2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      case 'user':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className={className} fill={isActive ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isActive ? 0 : 2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-b from-base-100 to-base-200/50">
      {/* Top Navbar - Glass Effect */}
      <header className="nav-glass sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-coral-400 to-mint-400 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <img src="/glovely.png" alt="Glovely" className="w-6 h-6" />
              </div>
              <span className="text-xl font-bold text-gradient-premium hidden sm:block">
                Glovely
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item flex items-center gap-2 text-sm font-medium ${isNavActive(item) ? 'active' : 'text-base-content/70 hover:text-base-content'
                    }`}
                >
                  {renderIcon(item.icon, isNavActive(item))}
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Points Badge (Desktop) */}
              {user && (
                <div className="hidden sm:flex points-badge cursor-help" title="Glovely Points">
                  <span className="text-lg">🪙</span>
                  <span className="font-bold">{points?.general_points?.toLocaleString() || 0}</span>
                </div>
              )}

              {/* Profile Dropdown */}
              <div className="dropdown dropdown-end">
                <button
                  tabIndex={0}
                  className="flex items-center gap-2 p-1 rounded-xl hover:bg-base-200/50 transition-colors"
                >
                  <div className="avatar">
                    <div className="w-9 h-9 rounded-xl overflow-hidden ring-2 ring-primary/20">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.username} className="object-cover" />
                      ) : (
                        <div className="bg-gradient-to-br from-coral-300 to-mint-300 text-white flex items-center justify-center w-full h-full text-sm font-bold">
                          {user?.username?.[0]?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content/50 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div
                  tabIndex={0}
                  className="dropdown-content z-[60] mt-3 w-64 rounded-2xl bg-base-100 shadow-xl border border-base-200 overflow-hidden"
                >
                  {/* User Info Header */}
                  <div className="p-4 bg-gradient-to-br from-coral-50 to-mint-50">
                    <div className="flex items-center gap-3">
                      <div className="avatar">
                        <div className="w-12 h-12 rounded-xl overflow-hidden">
                          {user?.picture ? (
                            <img src={user.picture} alt={user.username} className="object-cover" />
                          ) : (
                            <div className="bg-gradient-to-br from-coral-300 to-mint-300 text-white flex items-center justify-center w-full h-full text-lg font-bold">
                              {user?.username?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-base-content truncate">{user?.username}</p>
                        <p className="text-xs text-base-content/50 truncate">{user?.email}</p>
                      </div>
                    </div>

                    {/* Mobile Points */}
                    <div className="mt-3 sm:hidden">
                      <div className="points-badge w-full justify-center">
                        <span className="text-lg">🪙</span>
                        <span className="font-bold">{points?.general_points?.toLocaleString() || 0}</span>
                        <span className="text-xs text-amber-700/70">GP</span>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="p-2">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-base-200/50 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-base-content/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm">{t('nav.profileSettings')}</span>
                    </Link>

                    <Link
                      to="/my-persona"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-base-200/50 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-base-content/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">{t('nav.personaSettings')}</span>
                    </Link>

                    <div className="border-t border-base-200 my-2" />

                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-error/10 transition-colors text-error w-full"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span className="text-sm">{t('nav.logout')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-6 pb-24 md:pb-6">
        <div className="fade-in">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation - Premium Style */}
      <nav className="bottom-nav-premium fixed bottom-0 left-0 right-0 md:hidden z-50">
        <div className="flex items-center justify-around py-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`bottom-nav-item ${isNavActive(item) ? 'active' : ''}`}
            >
              <div className="nav-icon">
                {renderIcon(item.icon, isNavActive(item))}
              </div>
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  )
}
